import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        destructive:
          'border-transparent bg-red-100 text-red-700 border-red-200 [a&]:hover:bg-red-200 focus-visible:ring-red-200/20 dark:focus-visible:ring-red-200/40 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800',
        outline:
          'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        success:
          'border-transparent bg-green-100 text-green-700 border-green-200 [a&]:hover:bg-green-200 focus-visible:ring-green-200/20 dark:focus-visible:ring-green-200/40 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800',
        warning:
          'border-transparent bg-amber-100 text-amber-700 border-amber-200 [a&]:hover:bg-amber-200 focus-visible:ring-amber-200/20 dark:focus-visible:ring-amber-200/40 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800',
        info: 'border-transparent bg-blue-100 text-blue-700 border-blue-200 [a&]:hover:bg-blue-200 focus-visible:ring-blue-200/20 dark:focus-visible:ring-blue-200/40 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800',
        tertiary:
          'border-transparent bg-violet-100 text-violet-700 border-violet-200 [a&]:hover:bg-violet-200 focus-visible:ring-violet-200/20 dark:focus-visible:ring-violet-200/40 dark:bg-violet-900/20 dark:text-violet-300 dark:border-violet-800',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span';

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
